// App-wide "show me what's wrong" helper.
//
// When someone presses a submit-style button (Create / Save / Submit / Add /
// Update …) and the form answers with a validation message that's scrolled
// out of sight — e.g. "Assignee is required" at the top of the Add New Task
// modal while the user is looking at the Create Task button at the bottom —
// this scrolls the nearest scroll container (the modal body, the page …) so
// the first message is in the middle of the screen. If the message is
// already visible nothing moves.
//
// It is purely additive: it only listens to clicks/submits and reads the DOM
// that the forms already render. It never prevents an event, focuses a field
// or touches form state, so every form keeps its existing behaviour.
// Forms that report problems with toasts or alert() are unaffected (those are
// always on screen); native `required` validation is left to the browser,
// which already scrolls to the field.

// Button labels that mean "submit this". Matched against the start of the
// label, after any leading icon/"+".
const SUBMIT_LABEL_RE = new RegExp(
    '^(add|create|save|submit|update|send|confirm|upload|publish|assign|register|post|apply|approve|reject|accept|done|finish|complete|next|continue|proceed|raise|book|place|adjust|record|redeem|transfer|import|verify|mark|pin|login|log in|sign in|sign up|signup|check[ -]?in|check[ -]?out|ok|yes)\\b',
    'i'
);

// Cheap pre-filter for things that may be error messages / flagged fields.
// Every match is re-checked by isErrorElement().
const ERROR_SELECTOR = [
    '[class*="error" i]',
    '[class*="invalid" i]',
    '[class*="danger" i]',
    '[class*="destructive" i]',
    '[class*="text-red-" i]',
    '[class*="text-rose-" i]',
    '[class*="border-red-" i]',
    '[role="alert"]',
    '[aria-invalid="true"]',
    '[style*="color"]',
].join(',');

const FIELD_SELECTOR = 'input:not([type="hidden"]), select, textarea, [contenteditable="true"]';
const FIELD_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);
const NEVER_MESSAGE_TAGS = new Set([
    'BUTTON', 'A', 'OPTION', 'OPTGROUP', 'IMG', 'SVG', 'PATH', 'SCRIPT', 'STYLE',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TH', 'THEAD',
]);
// Class tokens that mean "this is an icon/button/badge/toast", not a message.
const NOT_A_MESSAGE_TOKEN_RE = /(^|[-_])(icon|btn|button|badge|chip|pill|toast|toaster|snackbar|tooltip)([-_]|$)/i;
// Class tokens of a modal-ish box: they bound where we look for errors.
const MODAL_TOKEN_RE = /(^|[-_])(modal|dialog|drawer|popup|offcanvas)$/i;

// How long after the click we keep waiting for (e.g. server-side) errors.
const WATCH_MS = 5000;
// Wait for the form to finish rendering a burst of changes before looking.
const SETTLE_MS = 60;

const classTokens = (el) => {
    const cls = typeof el.className === 'string' ? el.className : (el.getAttribute && el.getAttribute('class')) || '';
    return cls ? cls.split(/\s+/).filter(Boolean) : [];
};

const hasErrorClass = (el) => {
    const tokens = classTokens(el);
    if (!tokens.length || tokens.some((t) => NOT_A_MESSAGE_TOKEN_RE.test(t))) return false;
    return tokens.some((t) => (
        /error|invalid/i.test(t) ||
        /(^|[-_])(err|danger|destructive)([-_]|$)/i.test(t) ||
        /^(text|border)-(red|rose)-\d{2,3}$/i.test(t)
    ));
};

const isRedColor = (color) => {
    const parts = String(color || '').match(/[\d.]+/g);
    if (!parts || parts.length < 3) return false;
    const [r, g, b] = parts.map(Number);
    const alpha = parts.length > 3 ? Number(parts[3]) : 1;
    return alpha > 0.4 && r >= 150 && g <= 100 && b <= 100 && r - Math.max(g, b) >= 90;
};

const isRendered = (el) => {
    if (!el.getClientRects || el.getClientRects().length === 0) return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
};

const messageText = (el) => {
    const text = typeof el.innerText === 'string' ? el.innerText : el.textContent;
    return (text || '').trim();
};

// Real words, not just a red "*" required marker.
const hasMessageText = (el) => {
    const text = messageText(el);
    return text.length > 0 && text.length <= 500 && /\p{L}{2}/u.test(text);
};

const ownsText = (el) => Array.from(el.childNodes).some(
    (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
);

const isField = (el) => FIELD_TAGS.has(el.tagName) || el.isContentEditable;

const isScrollContainer = (style) => /(auto|scroll|overlay)/.test(style.overflowY);

const canScrollY = (node, style) => isScrollContainer(style) && node.scrollHeight > node.clientHeight + 1;

// Toasts and small fixed popups render on top of everything and are always
// visible, so they never need scrolling to. An error inside a scrollable
// modal body is *not* floating: the body can hide it.
const isFloating = (el) => {
    for (let node = el; node && node !== document.body; node = node.parentElement) {
        const style = window.getComputedStyle(node);
        if (node !== el && isScrollContainer(style)) return false;
        if (style.position === 'fixed') return true;
    }
    return false;
};

// Is `el` a validation problem we should bring into view?
//  - a field the form flagged (aria-invalid, has-error, border-red-500 …)
//  - a message: error-ish class, role="alert", or (when allowColor) plain
//    red text such as <small style={{ color: 'red' }}>.
const isErrorElement = (el, allowColor) => {
    if (!el || el.nodeType !== Node.ELEMENT_NODE || !el.isConnected) return false;
    if (isField(el)) {
        if (el.getAttribute('aria-invalid') !== 'true' && !hasErrorClass(el)) return false;
        return isRendered(el) && !isFloating(el);
    }
    if (NEVER_MESSAGE_TAGS.has(el.tagName)) return false;
    if (classTokens(el).some((t) => NOT_A_MESSAGE_TOKEN_RE.test(t))) return false;

    // Cheap checks first; computed styles only for the few that pass.
    const flagged = hasErrorClass(el) || el.getAttribute('role') === 'alert';
    if (!flagged && !(allowColor && ownsText(el))) return false;
    if (el.closest('button, a[href], select, svg')) return false;
    if (!hasMessageText(el) || !isRendered(el)) return false;
    if (!flagged) {
        if (el.closest('table, h1, h2, h3, h4, h5, h6')) return false;
        if (!isRedColor(window.getComputedStyle(el).color)) return false;
    }
    return !isFloating(el);
};

// A message that belongs to a form field (sits next to an input), as opposed
// to e.g. a "Failed to load" banner elsewhere on the page.
const isNextToField = (el) => {
    if (isField(el)) return true;
    for (let node = el.parentElement, depth = 0; node && depth < 2; node = node.parentElement, depth += 1) {
        if (node.querySelector(FIELD_SELECTOR)) return true;
    }
    return false;
};

const byDocumentOrder = (a, b) => {
    if (a === b) return 0;
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
};

// Errors that are already on screen in `scope` (used when a submit attempt
// re-shows the same messages, which produces no DOM change at all). Plain
// red inline-styled text only counts inside a form/modal, not page-wide.
const existingFieldErrors = (scope) => {
    const allowInlineColor = scope !== document.body;
    return Array.from(scope.querySelectorAll(ERROR_SELECTOR)).filter((el) => (
        isErrorElement(el, allowInlineColor && el.matches('[style*="color"]')) && isNextToField(el)
    ));
};

// A newly added message is a handful of nodes; a newly opened modal or page
// is much bigger, and red text in there (a red label, an "Urgent" chip …)
// isn't an error — only trust explicit error markup in big additions.
const SMALL_ADDITION = 25;

// Errors added/changed by the DOM mutations since the click.
const freshErrors = (roots) => {
    const found = new Set();
    let checked = 0;
    roots.forEach((root) => {
        if (!root.isConnected) return;
        const errorAncestor = root.parentElement && root.parentElement.closest(ERROR_SELECTOR);
        if (isErrorElement(errorAncestor, false)) found.add(errorAncestor);
        const nodes = [root, ...root.querySelectorAll('*')];
        const allowColor = nodes.length <= SMALL_ADDITION;
        for (const node of nodes) {
            if (checked > 3000) return;
            checked += 1;
            if (isErrorElement(node, allowColor)) found.add(node);
        }
    });
    return Array.from(found);
};

// Did a removed node carry an error? (The user fixed one field but another is
// still wrong → the form re-validated, so look at what's left.)
const removedAnError = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    if (hasErrorClass(node) || node.getAttribute('role') === 'alert') return true;
    return Array.from(node.querySelectorAll('*')).some((n) => hasErrorClass(n) || n.getAttribute('role') === 'alert');
};

const clipRectOf = (node) => {
    const box = node.getBoundingClientRect();
    const top = box.top + node.clientTop;
    return { top, bottom: top + node.clientHeight };
};

// Covered by a fixed header / sticky bar even though it's inside the
// scroll area?
const isCovered = (el, rect) => {
    if (typeof document.elementFromPoint !== 'function') return false;
    const x = Math.min(Math.max(rect.left + Math.min(rect.width / 2, 40), 0), window.innerWidth - 1);
    const y = rect.top + rect.height / 2;
    if (y < 0 || y >= window.innerHeight) return false; // off screen: handled by the bounds check
    const hit = document.elementFromPoint(x, y);
    return !!hit && !el.contains(hit) && !hit.contains(el);
};

const isFullyVisible = (el) => {
    const rect = el.getBoundingClientRect();
    for (let node = el.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
        const style = window.getComputedStyle(node);
        if (style.overflowY !== 'visible') {
            const clip = clipRectOf(node);
            if (rect.top < clip.top - 1 || rect.bottom > clip.bottom + 1) return false;
        }
    }
    if (rect.top < -1 || rect.bottom > window.innerHeight + 1) return false;
    return !isCovered(el, rect);
};

const prefersReducedMotion = () => (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Centre `el` in each scroll container that hides it (inner first), and in
// the window unless it lives in a fixed modal.
export const revealElement = (el) => {
    if (!el || !el.isConnected || isFullyVisible(el)) return false;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    const rect = el.getBoundingClientRect();
    let { top, bottom } = rect;
    let mustCentre = isCovered(el, rect);
    let scrolled = false;

    for (let node = el.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
        const style = window.getComputedStyle(node);
        if (canScrollY(node, style)) {
            const clip = clipRectOf(node);
            if (mustCentre || top < clip.top || bottom > clip.bottom) {
                const wanted = (top + bottom) / 2 - (clip.top + clip.bottom) / 2;
                const delta = clamp(wanted, -node.scrollTop, node.scrollHeight - node.clientHeight - node.scrollTop);
                if (Math.abs(delta) >= 1) {
                    node.scrollBy({ top: delta, behavior });
                    top -= delta;
                    bottom -= delta;
                    scrolled = true;
                    mustCentre = false;
                }
            }
        }
        // Content of a fixed modal doesn't move with the page.
        if (style.position === 'fixed') return scrolled;
    }

    if (mustCentre || top < 0 || bottom > window.innerHeight) {
        const page = document.scrollingElement || document.documentElement;
        const scrollY = window.pageYOffset || page.scrollTop || 0;
        const wanted = (top + bottom) / 2 - window.innerHeight / 2;
        const delta = clamp(wanted, -scrollY, page.scrollHeight - window.innerHeight - scrollY);
        if (Math.abs(delta) >= 1) {
            window.scrollBy({ top: delta, behavior });
            scrolled = true;
        }
    }
    return scrolled;
};

// Where to look for the errors of this form: the <form>, dialog or modal box
// that holds the button, else the whole page.
const scopeFor = (control) => {
    for (let node = control; node && node !== document.body; node = node.parentElement) {
        if (node.tagName === 'FORM' || node.tagName === 'DIALOG') return node;
        if (node.matches('[role="dialog"], [role="alertdialog"], [aria-modal="true"]')) return node;
        if (classTokens(node).some((t) => MODAL_TOKEN_RE.test(t))) return node;
        if (window.getComputedStyle(node).position === 'fixed') return node;
    }
    return document.body;
};

const controlLabel = (control) => {
    const text = control.tagName === 'INPUT'
        ? control.value
        : messageText(control) || control.getAttribute('aria-label') || control.getAttribute('title');
    return (text || '').replace(/^[^\p{L}]+/u, '').replace(/\s+/g, ' ').trim();
};

const isSubmitControl = (control) => {
    if (!control || control.disabled) return false;
    const type = (control.getAttribute('type') || '').toLowerCase();
    if (control.tagName === 'INPUT') {
        if (type === 'submit' || type === 'image') return true;
        if (type !== 'button') return false;
    } else if (control.tagName === 'BUTTON' && control.form && (type === '' || type === 'submit')) {
        return true;
    }
    return SUBMIT_LABEL_RE.test(controlLabel(control));
};

let session = null;

const endSession = () => {
    if (!session) return;
    const s = session;
    session = null;
    s.observer.disconnect();
    clearTimeout(s.settleTimer);
    clearTimeout(s.expiryTimer);
    s.stopListeners();
};

const evaluate = (s, initial) => {
    if (session !== s) return;
    if (s.browserHandledIt) { endSession(); return; }

    const { scope } = s;
    const records = s.pending.concat(s.observer.takeRecords());
    s.pending = [];
    const roots = new Set();
    records.forEach((record) => {
        const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
        if (!target || !scope.contains(target)) return;
        s.changedInScope = true;
        if (record.type === 'childList') {
            record.addedNodes.forEach((n) => roots.add(n.nodeType === Node.ELEMENT_NODE ? n : target));
            record.removedNodes.forEach((n) => { if (removedAnError(n)) s.errorRemoved = true; });
        } else {
            roots.add(target);
        }
    });

    const fresh = freshErrors(roots).filter((el) => scope.contains(el));
    // Re-check what's already shown when this attempt produced new errors,
    // removed some (others remain), or changed nothing at all (same errors
    // shown again). Something like "Add another row" changes the DOM without
    // touching errors, so it doesn't scroll anywhere.
    const recheckExisting = fresh.length > 0 || (initial && (!s.changedInScope || s.errorRemoved));
    if (!recheckExisting) {
        if (s.control && !s.control.isConnected) endSession(); // modal closed / page changed
        return;
    }

    const candidates = new Set(fresh);
    existingFieldErrors(scope).forEach((el) => candidates.add(el));
    const first = Array.from(candidates).sort(byDocumentOrder)[0];
    if (!first) {
        if (s.control && !s.control.isConnected) endSession();
        return;
    }
    endSession();
    revealElement(first);
};

const startSession = (control, form) => {
    endSession();
    const scope = scopeFor(control || form);
    const s = {
        control,
        scope,
        pending: [],
        changedInScope: false,
        errorRemoved: false,
        browserHandledIt: false,
        startedAt: Date.now(),
    };

    let initialDone = false;
    const schedule = () => {
        clearTimeout(s.settleTimer);
        s.settleTimer = setTimeout(() => {
            const initial = !initialDone;
            initialDone = true;
            evaluate(s, initial);
        }, SETTLE_MS);
    };

    s.observer = new MutationObserver((records) => {
        s.pending.push(...records);
        // Only the first look waits for the burst of changes to settle;
        // later (server) errors are handled as they arrive.
        if (initialDone) schedule();
    });
    s.observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-invalid', 'hidden'],
    });

    // The user moved on (scrolled, typed, clicked elsewhere): don't yank the
    // page around for a late response.
    const stop = () => { if (session === s) endSession(); };
    const userEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    userEvents.forEach((type) => document.addEventListener(type, stop, true));
    s.stopListeners = () => userEvents.forEach((type) => document.removeEventListener(type, stop, true));

    s.expiryTimer = setTimeout(stop, WATCH_MS);
    session = s;
    schedule();
};

const onClick = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const target = event.target && event.target.nodeType === Node.ELEMENT_NODE ? event.target : event.target && event.target.parentElement;
    const control = target && target.closest('button, input[type="submit"], input[type="button"], input[type="image"], [role="button"]');
    if (!isSubmitControl(control)) return;
    startSession(control, null);
};

const onSubmit = (event) => {
    // Enter-key submits also fire a click on the submit button; only handle
    // forms without one here.
    if (session && Date.now() - session.startedAt < 100) return;
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    startSession(event.submitter || null, form);
};

// Native `required`/`pattern` validation: the browser already scrolls to and
// focuses the field, so stay out of its way.
const onInvalid = () => {
    if (session) session.browserHandledIt = true;
};

let installed = false;

export const installScrollToFirstError = () => {
    if (installed || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return () => {};
    installed = true;
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('invalid', onInvalid, true);
    return () => {
        endSession();
        document.removeEventListener('click', onClick, true);
        document.removeEventListener('submit', onSubmit, true);
        document.removeEventListener('invalid', onInvalid, true);
        installed = false;
    };
};

export default installScrollToFirstError;
